import Color from 'color';

import * as globals from '../css/globals.styles';

export function colorN(
  index: number,
  palette: readonly string[],
  alpha?: number
): string {
  /**
   * Given an index and a palette, returns a color.
   */
  const c = Color(palette[index % palette.length]);
  return c
    .alpha(alpha || c.alpha())
    .rgb()
    .string();
}

export type RGB = [number, number, number];
export const COLORS16 = [
  '#F07FDD', // pink
  '#C2327A', // maroon
  '#F0434F', // red
  '#FAB796', // peach
  '#E57439', // orange
  '#FFB83D', // yellow
  '#9BC750', // lime
  '#479A5F', // kelly green
  '#87CEBF', // seafoam
  '#229487', // forest
  '#5BC5DB', // cyan
  '#538AE5', // blue
  '#865ED6', // purple
  '#DC4BDC', // magenta
  '#AD6F50', // brown
  '#A1A9AD', // gray
] as const;

export type DefaultColor = (typeof COLORS16)[number];

export const COLORS16_IN_SPECTRUM_ORDER: DefaultColor[] = [
  '#F0434F', // Red
  '#E57439', // Orange
  '#FAB796', // Peach
  '#FFB83D', // Yellow
  '#9BC750', // Lime
  '#479A5F', // Kelly green
  '#229487', // Forest
  '#87CEBF', // Seafoam
  '#5BC5DB', // Cyan
  '#538AE5', // Blue
  '#865ED6', // Purple
  '#F07FDD', // Pink
  '#DC4BDC', // Magenta
  '#C2327A', // Maroon
  '#AD6F50', // Brown
  '#A1A9AD', // Gray
];

// Deuteronomaly is the most common form of color blindness. We offer this palette because it
// has a wider range of colors vs the OTHER_COLOR_BLIND_PALETTE.
export const COMMON_DEUTERONOMALY_PALETTE_SPECTRUM_ORDER = [
  globals.SIENNA_650,
  globals.SIENNA_550,
  globals.SIENNA_450,
  globals.SIENNA_350,
  globals.GOLD_400,
  globals.CACTUS_450,
  globals.GREEN_400,
  globals.GREEN_500,
  globals.GREEN_600,
  globals.BLUE_400,
  globals.BLUE_500,
  globals.BLUE_600,
  globals.MAGENTA_650,
  globals.MAGENTA_450,
  globals.MOON_400,
  globals.MOON_550,
] as const;

type CommonDeuteronomalyPalette =
  (typeof COMMON_DEUTERONOMALY_PALETTE_SPECTRUM_ORDER)[number];

export const COMMON_DEUTERONOMALY_PALETTE_CYCLE_ORDER: CommonDeuteronomalyPalette[] =
  [
    globals.MAGENTA_650,
    globals.SIENNA_350,
    globals.SIENNA_550,
    globals.GOLD_400,
    globals.BLUE_500,
    globals.GREEN_600,
    globals.CACTUS_450,
    globals.MOON_400,
    globals.BLUE_400,
    globals.BLUE_600,
    globals.GREEN_500,
    globals.GREEN_400,
    globals.MAGENTA_450,
    globals.SIENNA_450,
    globals.SIENNA_650,
    globals.MOON_550,
  ];

// This palette has been tested with deuteronopia, protanopia, and tritanopia simulators. Those forms of color blindness
// are less common than deuteronomaly. This palette is another option in case the COMMON_DEUTERONOMALY_PALETTE
// is not sufficient for a user. This one focuses on yellows/blues/grays only which are commonly colorblind-friendly
// across most forms of color blindness.
export const OTHER_COLOR_BLIND_PALETTE_SPECTRUM_ORDER = [
  globals.SIENNA_650,
  globals.SIENNA_600,
  globals.SIENNA_550,
  globals.SIENNA_450,
  globals.SIENNA_400,
  globals.SIENNA_350,
  globals.SIENNA_300,
  globals.BLUE_350,
  globals.BLUE_400,
  globals.BLUE_450,
  globals.BLUE_500,
  globals.BLUE_550,
  globals.BLUE_600,
  globals.MOON_400,
  globals.MOON_500,
  globals.MOON_600,
] as const;

type OtherColorBlindPalette =
  (typeof OTHER_COLOR_BLIND_PALETTE_SPECTRUM_ORDER)[number];

export const OTHER_COLOR_BLIND_PALETTE_CYCLE_ORDER: OtherColorBlindPalette[] = [
  globals.SIENNA_550,
  globals.BLUE_550,
  globals.BLUE_450,
  globals.SIENNA_650,
  globals.SIENNA_400,
  globals.SIENNA_300,
  globals.MOON_400,
  globals.MOON_500,
  globals.BLUE_400,
  globals.BLUE_600,
  globals.SIENNA_600,
  globals.SIENNA_450,
  globals.SIENNA_350,
  globals.BLUE_350,
  globals.BLUE_500,
  globals.MOON_600,
];

export function colorNRGB(
  index: number,
  palette: string[],
  alpha?: number
): RGB {
  /**
   * Given an index and a palette, returns a color.
   */
  const c = Color(palette[index % palette.length]);
  return c
    .alpha(alpha || c.alpha())
    .rgb()
    .array() as [number, number, number];
}

// Our bespoke palette. This is in round-robin order.
export const ROBIN16 = [
  11, // blue
  2, // red
  7, // kelly green
  12, // purple
  0, // pink
  4, // orange
  8, // seafoam
  13, // magenta
  5, // yellow
  10, // cyan
  9, // forest
  3, // peach
  6, // lime
  14, // brown
  1, // maroon
  15, // gray
].map(i => COLORS16[i]);
export const GLOBAL_COLORS = {
  primary: Color(globals.primary),
  outline: Color('rgb(219, 219, 219)'),
  linkBlue: Color(globals.primaryText),
  lightGray: Color('#B3B3B0'),
  gray: Color(globals.gray600),
  background: globals.gray50,
};

export function colorFromString(s: string): RGB {
  return Color(s).rgb().array() as [number, number, number];
}

function hashString(s: string) {
  return s.split('').reduce((a, b) => {
    a = (a << 10) - a + b.charCodeAt(0);
    return a & a;
  }, 0);
}

export function colorFromName(
  name: string,
  alpha?: number,
  palette?: readonly string[]
) {
  /**
   * Hashes a given run name and returns a corresponding color.
   */
  const idx = Math.abs(hashString(name));
  return colorN(idx, palette ?? COLORS16, alpha);
}
